<!--
  Landing page. Editorial-leaning hero pulls visitors into a single
  primary action; "by the numbers" stat row reads as a financial
  summary instead of a generic SaaS strip.

  No data fetches happen here; pure marketing/orientation copy. The
  stats are a manual snapshot of the dataset — they only need to
  be roughly right and they drift only by fractions per week.
-->
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { api } from '../api';
import type { LifterSearchRow } from '../api';
import { vReveal } from '../composables/reveal';

type FeatureTab = 'trends' | 'career' | 'analysis' | 'lifter';

// Routing is owned by App; the home page just signals intent.
const emit = defineEmits<{
  (e: 'navigate', tab: FeatureTab): void;
  (e: 'open-lifter', id: number): void;
  (e: 'open-section', payload: { tab: FeatureTab; hash?: string }): void;
}>();

// Each "gym floor argument" links to the exact place the site answers it.
const POINTS: { tag: string; question: string; answer: string; tab: FeatureTab; hash?: string; accent: string; cta: string }[] = [
  {
    tag: 'Sport health',
    question: 'Is the sport actually dying?',
    answer: 'Every so often someone declares powerlifting dead. Trends plots distinct lifters per year for whatever slice you pick — the count rarely agrees with the eulogy.',
    tab: 'trends', hash: 'boom', accent: 'var(--accent)', cta: 'See the participation curve',
  },
  {
    tag: 'Career trajectory',
    question: 'What should meet #5 actually look like?',
    answer: "Career Arc traces the typical Dots curve from debut through meet 30+, with a p25–p75 band so you're measuring against the real spread, not the genetic outliers filling your feed.",
    tab: 'career', hash: 'climb', accent: 'var(--accent-teal)', cta: 'See the average arc',
  },
  {
    tag: 'Predictive signal',
    question: 'Does debuting young raise your ceiling?',
    answer: 'Analysis runs the regression and states it in plain English — effect size first, so a tiny result on a huge sample stops passing itself off as "highly significant."',
    tab: 'analysis', hash: 'career-predictors', accent: 'var(--accent-amber)', cta: 'See the regression',
  },
  {
    tag: 'Personal benchmark',
    question: 'Where does your last meet really rank?',
    answer: 'Lifter Lookup drops your Dots into its exact slice — event, equipment, weight class, division — and grades every meet against others at the same stage of their career.',
    tab: 'lifter', accent: 'var(--accent-violet)', cta: 'Look yourself up',
  },
];

// ─── Inline lifter lookup (hero) ─────────────────────────────────────────
// The fastest path to a lifter's page: type a name, pick from live results,
// land on their profile. Debounced so we don't fire a query per keystroke;
// a request-id guard drops out-of-order responses.
const query      = ref('');
const results    = ref<LifterSearchRow[]>([]);
const searching  = ref(false);
const resultsOpen = ref(false);
const activeIdx  = ref(-1);
let searchDebounce: number | null = null;
let searchReqId = 0;

function onSearchInput() {
  if (searchDebounce !== null) clearTimeout(searchDebounce);
  const q = query.value.trim();
  if (q.length < 2) { results.value = []; resultsOpen.value = false; searching.value = false; return; }
  searching.value = true;
  resultsOpen.value = true;
  searchDebounce = window.setTimeout(runSearch, 220);
}

async function runSearch() {
  const q = query.value.trim();
  if (q.length < 2) return;
  const reqId = ++searchReqId;
  try {
    const res = await api.searchLifters(q, { limit: 6 });
    if (reqId !== searchReqId) return; // stale — a newer keystroke won
    results.value = res.lifters;
    activeIdx.value = -1;
    resultsOpen.value = true;
  } catch {
    if (reqId === searchReqId) results.value = [];
  } finally {
    if (reqId === searchReqId) searching.value = false;
  }
}

function choose(l: LifterSearchRow) {
  resultsOpen.value = false;
  query.value = l.name;
  emit('open-lifter', l.lifter_id);
}

function onSearchKeydown(e: KeyboardEvent) {
  if (!resultsOpen.value || !results.value.length) {
    if (e.key === 'Enter') runSearch();
    return;
  }
  if (e.key === 'ArrowDown')      { e.preventDefault(); activeIdx.value = Math.min(activeIdx.value + 1, results.value.length - 1); }
  else if (e.key === 'ArrowUp')   { e.preventDefault(); activeIdx.value = Math.max(activeIdx.value - 1, 0); }
  else if (e.key === 'Enter')     { e.preventDefault(); choose(results.value[activeIdx.value >= 0 ? activeIdx.value : 0]); }
  else if (e.key === 'Escape')    { resultsOpen.value = false; }
}

function fmtDots(v: number | null): string {
  return v == null ? '—' : Math.round(v).toString();
}

// Four tools, four "lenses" — each gets a signature hue from the chart
// palette (the section literally frames them as four lenses on one dataset)
// and links straight to its page.
const FEATURES: { title: string; tab: FeatureTab; accent: string; cta: string; summary: string; bullets: string[] }[] = [
  {
    title: 'Trends',
    tab: 'trends',
    accent: 'var(--accent)',
    cta: 'Explore trends',
    summary: 'Year-over-year movement of the sport. Participation, average strength, federation share, sliced any way you want.',
    bullets: [
      'Plot any group of lifters against any year range',
      'Group by weight class, equipment, federation, sex, or age class',
      'Catch shifts the gym-floor narrative misses',
    ],
  },
  {
    title: 'Career Arc',
    tab: 'career',
    accent: 'var(--accent-teal)',
    cta: 'Trace a career',
    summary: 'What a typical career actually looks like, meet by meet and age by age, for the group of lifters you care about.',
    bullets: [
      'Average Dots curve from debut through meet 30+',
      'p25 / p75 bands so you see the realistic range',
      'Retention curves: how many lifters reach meet 5? meet 10?',
    ],
  },
  {
    title: 'Analysis',
    tab: 'analysis',
    accent: 'var(--accent-amber)',
    cta: 'Run the numbers',
    summary: 'Linear regression over millions of entries with a plain-English verdict. No PhD required.',
    bullets: [
      'Effect size first, p-value second. No "very significant" labels on trivial effects',
      'Tests participation vs strength, debut age vs peak, career length predictors',
      'Every regression card explains itself',
    ],
  },
  {
    title: 'Lifter Lookup',
    tab: 'lifter',
    accent: 'var(--accent-violet)',
    cta: 'Find a lifter',
    summary: 'Pull up any lifter\'s career and benchmark every meet against the group of lifters that actually compares.',
    bullets: [
      'Percentile by Dots within event, equipment, weight class, and division',
      'Per-meet "vs avg" tile showing your delta and rank within the group',
      'Benchmark your debut against other debuts',
    ],
  },
];

// Editorial stats — label on left, number flush right.
//
// IMPORTANT: most of these are MEDIANS, not means. Career-length
// distributions are heavily right-skewed (long tail of veterans
// with 30+ meets pulls means up): median total meets is 2.0, mean
// is 3.6. The median is the honest "typical lifter" number.
// YOY participation growth uses the mean of yearly %-changes since
// each year's growth rate is itself a single observation.
//
// The entry COUNT is the one figure that drifts each crawl, so it's pulled
// live from /meta (data_freshness.total_entries) rather than hardcoded — no
// more stale "3.9M" as the corpus grows. The medians barely move between
// crawls and stay as manual snapshots.
const entriesLabel = ref('3.9M');
onMounted(async () => {
  try {
    const m = await api.meta();
    const n = m.data_freshness?.total_entries;
    if (n && n > 0) {
      entriesLabel.value = n >= 1_000_000
        ? (n / 1_000_000).toFixed(1) + 'M'
        : n.toLocaleString();
    }
  } catch { /* keep the fallback snapshot */ }
});

const STATS = computed(() => [
  { value: entriesLabel.value, label: 'Meet entries indexed' },
  { value: '2.0',    label: 'Median meets per career' },
  { value: '23.5',   label: 'Median first-meet age' },
  { value: '3.1',    label: 'Avg meets to reach 400 Dots' },
  { value: '+13.1%', label: 'Avg YoY participation growth (last 20y)' },
]);
</script>

<template>
  <div class="home">
    <!-- ───── HERO ─────
         Single dominant headline, single primary CTA, secondary
         action as a quiet ghost link. Background carries a faint
         sparkline drawn from real yearly participation data
         (smoothed) — gives the hero a contextual "this is a data
         site" mark without the previous oversized numeral feeling
         arbitrary. -->
    <section class="hero">
      <!--
        Hero banner: the sport's REAL participation ridge — distinct lifters
        per year, 1994–2025, straight from the dataset (GET /trends/journey,
        all_lifters). Baked in as a static path at build time so the home
        page makes ZERO API calls and paints instantly; it's a point-in-time
        snapshot, not a live chart. You can read the story in it: a slow rise
        through the 2000s, the steep 2010s climb, the 2020 COVID dip, then the
        recovery to an all-time high in 2024. (The partial current year is
        trimmed so the curve doesn't look like a crash.) The line strokes
        itself in via pathLength/dashoffset; area + markers fade in behind it.
        Reduced motion shows the final state with no animation.
      -->
      <svg class="hero-bg-chart" viewBox="0 0 1200 460" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
        <defs>
          <linearGradient id="hero-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stop-color="#f78166" stop-opacity="0.20" />
            <stop offset="100%" stop-color="#f78166" stop-opacity="0" />
          </linearGradient>
          <radialGradient id="hero-glow" cx="87%" cy="20%" r="52%">
            <stop offset="0%"   stop-color="#f78166" stop-opacity="0.13" />
            <stop offset="100%" stop-color="#f78166" stop-opacity="0" />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width="1200" height="460" fill="url(#hero-glow)" />
        <g class="hero-grid">
          <line x1="0" x2="1200" y1="120" y2="120" />
          <line x1="0" x2="1200" y1="215" y2="215" />
          <line x1="0" x2="1200" y1="310" y2="310" />
          <line x1="0" x2="1200" y1="405" y2="405" />
        </g>
        <path class="hero-area" fill="url(#hero-fill)"
              d="M 0,416.4 C 6.5,415.3 25.8,410.3 38.7,409.7 C 51.6,409.1 64.5,412.8 77.4,413.1 C 90.3,413.5 103.2,412.2 116.1,411.8 C 129.0,411.4 141.9,411.2 154.8,410.9 C 167.7,410.6 180.6,410.4 193.5,410 C 206.4,409.6 219.4,409.3 232.3,408.2 C 245.2,407.1 258.1,404.9 271,403.4 C 283.9,401.9 296.8,400.3 309.7,399.3 C 322.6,398.4 335.5,397.9 348.4,397.7 C 361.3,397.5 374.2,398.1 387.1,398 C 400.0,397.9 412.9,397.3 425.8,397.2 C 438.7,397.1 451.6,397.9 464.5,397.6 C 477.4,397.3 490.3,396.1 503.2,395.5 C 516.1,394.9 529.0,394.3 541.9,393.8 C 554.8,393.3 567.7,398.0 580.6,392.3 C 593.5,386.6 606.5,366.3 619.4,359.5 C 632.3,352.7 645.2,353.8 658.1,351.3 C 671.0,348.8 683.9,348.4 696.8,344.7 C 709.7,341.0 722.6,338.3 735.5,329.1 C 748.4,319.9 761.3,301.3 774.2,289.3 C 787.1,277.3 800.0,267.1 812.9,257.1 C 825.8,247.1 838.7,237.2 851.6,229.2 C 864.5,221.2 877.4,215.4 890.3,209.2 C 903.2,203.0 916.1,197.8 929,191.8 C 941.9,185.9 954.8,159.2 967.7,173.5 C 980.6,187.8 993.6,268.3 1006.5,277.5 C 1019.4,286.7 1032.3,247.9 1045.2,228.7 C 1058.1,209.4 1071.0,182.3 1083.9,162 C 1096.8,141.7 1109.7,117.8 1122.6,106.8 C 1135.5,95.8 1148.4,97.3 1161.3,96 C 1174.2,94.7 1193.5,98.3 1200,98.8 L 1200,460 L 0,460 Z" />
        <path class="hero-line" pathLength="1" fill="none"
              d="M 0,416.4 C 6.5,415.3 25.8,410.3 38.7,409.7 C 51.6,409.1 64.5,412.8 77.4,413.1 C 90.3,413.5 103.2,412.2 116.1,411.8 C 129.0,411.4 141.9,411.2 154.8,410.9 C 167.7,410.6 180.6,410.4 193.5,410 C 206.4,409.6 219.4,409.3 232.3,408.2 C 245.2,407.1 258.1,404.9 271,403.4 C 283.9,401.9 296.8,400.3 309.7,399.3 C 322.6,398.4 335.5,397.9 348.4,397.7 C 361.3,397.5 374.2,398.1 387.1,398 C 400.0,397.9 412.9,397.3 425.8,397.2 C 438.7,397.1 451.6,397.9 464.5,397.6 C 477.4,397.3 490.3,396.1 503.2,395.5 C 516.1,394.9 529.0,394.3 541.9,393.8 C 554.8,393.3 567.7,398.0 580.6,392.3 C 593.5,386.6 606.5,366.3 619.4,359.5 C 632.3,352.7 645.2,353.8 658.1,351.3 C 671.0,348.8 683.9,348.4 696.8,344.7 C 709.7,341.0 722.6,338.3 735.5,329.1 C 748.4,319.9 761.3,301.3 774.2,289.3 C 787.1,277.3 800.0,267.1 812.9,257.1 C 825.8,247.1 838.7,237.2 851.6,229.2 C 864.5,221.2 877.4,215.4 890.3,209.2 C 903.2,203.0 916.1,197.8 929,191.8 C 941.9,185.9 954.8,159.2 967.7,173.5 C 980.6,187.8 993.6,268.3 1006.5,277.5 C 1019.4,286.7 1032.3,247.9 1045.2,228.7 C 1058.1,209.4 1071.0,182.3 1083.9,162 C 1096.8,141.7 1109.7,117.8 1122.6,106.8 C 1135.5,95.8 1148.4,97.3 1161.3,96 C 1174.2,94.7 1193.5,98.3 1200,98.8" />
        <g class="hero-marks">
          <circle class="hero-dot" cx="348.4" cy="397.7" r="2.5" />
          <circle class="hero-dot" cx="735.5" cy="329.1" r="2.5" />
          <circle class="hero-dot" cx="1006.5" cy="277.5" r="2.5" />
          <circle class="hero-peak-halo" cx="1161.3" cy="96" r="9" />
          <circle class="hero-peak" cx="1161.3" cy="96" r="3.5" />
        </g>
      </svg>
      <div class="hero-content">
        <div class="hero-eyebrow reveal" style="--d: 0ms">
          <span class="dot" /> Powerlifting · By the numbers
        </div>
        <h1 class="hero-title">
          <span class="hero-line-1 reveal" style="--d: 80ms">Powerlifting is</span>
          <span class="hero-line-2 reveal" style="--d: 200ms">
            <span class="display-italic">alive,</span> and
            <span class="display-italic">getting&nbsp;stronger.</span>
          </span>
        </h1>
        <p class="hero-sub reveal" style="--d: 380ms">
          Every drug-tested and untested meet on record, turned into trends,
          career-arc benchmarks, and hard statistics. Or search a million
          lifters and see how any career ranks, meet by meet.
        </p>
        <div class="hero-search reveal" style="--d: 480ms" :class="{ open: resultsOpen && (results.length || (query.trim().length >= 2 && !searching)) }">
          <div class="hs-field">
            <svg class="hs-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              v-model="query"
              type="text"
              class="hs-input"
              placeholder="Look up any lifter by name"
              aria-label="Search lifters by name"
              role="combobox"
              :aria-expanded="resultsOpen"
              aria-controls="hero-search-results"
              autocomplete="off"
              @input="onSearchInput"
              @keydown="onSearchKeydown"
              @focus="onSearchInput"
              @blur="resultsOpen = false"
            />
            <span v-if="searching" class="hs-spin" aria-hidden="true"></span>
          </div>
          <ul v-if="resultsOpen && results.length" id="hero-search-results" class="hs-results" role="listbox">
            <li
              v-for="(l, i) in results"
              :key="l.lifter_id"
              class="hs-row"
              :class="{ active: i === activeIdx }"
              role="option"
              :aria-selected="i === activeIdx"
              @mousedown.prevent="choose(l)"
              @mouseenter="activeIdx = i"
            >
              <span class="hs-name">{{ l.name }}</span>
              <span class="hs-meta">
                <span class="num hs-dots">{{ fmtDots(l.pr_dots) }}</span> Dots
                <span class="hs-dot-sep">·</span>
                <span class="num">{{ l.meet_count }}</span> {{ Number(l.meet_count) === 1 ? 'meet' : 'meets' }}
              </span>
            </li>
          </ul>
          <div v-else-if="resultsOpen && query.trim().length >= 2 && !searching" class="hs-empty">
            No lifters match “{{ query.trim() }}”. Try a last name.
          </div>
        </div>
        <div class="hero-cta reveal" style="--d: 600ms">
          <a href="/analysis" class="cta-primary" @click.prevent="emit('navigate', 'analysis')">
            Explore the analysis
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </a>
          <a href="/trends" class="cta-secondary" @click.prevent="emit('navigate', 'trends')">Browse the trends →</a>
        </div>
      </div>
    </section>

    <!-- ───── STATS — editorial table ─────
         Reads like a financial summary: label on the left, number
         flush right. Each row a separate beat of the dataset. -->
    <section class="stats reveal" style="--d: 700ms">
      <div class="stats-eyebrow">By the numbers</div>
      <dl class="stats-table">
        <div v-for="s in STATS" :key="s.label" class="stat-row">
          <dt>{{ s.label }}</dt>
          <dd class="num">{{ s.value }}</dd>
        </div>
      </dl>
    </section>

    <hr class="hr-soft" />

    <!-- ───── Provocative talking points ───── -->
    <section class="talking-points">
      <div class="section-eyebrow">What can the data tell you?</div>
      <h2 class="section-headline">
        The arguments the gym floor keeps having, <span class="display-italic">settled with data.</span>
      </h2>
      <div class="point-grid">
        <a
          v-for="(p, i) in POINTS"
          :key="p.question"
          class="point"
          :href="p.hash ? `/${p.tab}#${p.hash}` : `/${p.tab}`"
          :style="{ '--fa': p.accent }"
          v-reveal="{ delay: i * 90 }"
          @click.prevent="emit('open-section', { tab: p.tab, hash: p.hash })"
        >
          <div class="point-tag"><span class="dot" /> {{ p.tag }}</div>
          <h3>{{ p.question }}</h3>
          <p>{{ p.answer }}</p>
          <span class="point-go">
            {{ p.cta }}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </span>
        </a>
      </div>
    </section>

    <hr class="hr-soft" />

    <!-- ───── Feature highlights — typographic, not card-bound ───── -->
    <section class="features">
      <div class="section-eyebrow">What's inside</div>
      <h2 class="section-headline">
        Four lenses on the <span class="display-italic">same data.</span>
      </h2>
      <div class="feature-grid">
        <a
          v-for="(f, i) in FEATURES"
          :key="f.title"
          class="feature"
          :href="'/' + f.tab"
          :style="{ '--fa': f.accent }"
          v-reveal="{ delay: i * 90 }"
          @click.prevent="emit('navigate', f.tab)"
        >
          <span class="feature-num num">0{{ i + 1 }}</span>
          <h3>{{ f.title }}</h3>
          <p class="feature-summary">{{ f.summary }}</p>
          <ul>
            <li v-for="b in f.bullets" :key="b">{{ b }}</li>
          </ul>
          <span class="feature-go">
            {{ f.cta }}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </span>
        </a>
      </div>
    </section>

    <hr class="hr-soft" />

    <!-- ───── Contact ───── -->
    <section class="contact">
      <div class="section-eyebrow">Got an idea?</div>
      <h2 class="section-headline">
        <span class="display-italic">Send</span> it.
      </h2>
      <p>
        Missing an analysis you'd find useful? Spotted a regression that doesn't smell right? Want a
        new way to filter? Email
        <a href="mailto:ideas@powerliftingtrends.com" class="contact-email">ideas@powerliftingtrends.com</a>.
        Every email gets read; the best ideas tend to land in the next deploy.
      </p>
    </section>
  </div>
</template>

<style scoped>
.home {
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 4px;
  display: flex;
  flex-direction: column;
}

/* ───── HERO ─────
   Asymmetric layout: content left-anchored, oversized numeral
   sitting behind everything as a textural element. */
.hero {
  position: relative;
  padding: 100px 32px 80px;
  margin-bottom: 36px;
  overflow: hidden;
  isolation: isolate;
}
/* SVG sparkline anchored to the bottom-right of the hero. Stretched
   wide so the rising curve sweeps from the lower-left under the
   headline to the top-right shoulder of the section — gives the eye
   an upward gesture without competing with the title. */
.hero-bg-chart {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  user-select: none;
}
.hero-grid line {
  stroke: rgba(255, 255, 255, 0.05);
  stroke-width: 1;
  stroke-dasharray: 2 7;
}
/* The ridge reads first as atmosphere, second as "this is a data site."
   The line strokes itself in on load (pathLength=1 + dashoffset); the area
   and end markers fade in just behind it. Opacities stay low enough that
   the headline still wins the eye. */
.hero-area { opacity: 0.85; animation: hero-fade-in 1.3s ease 0.9s both; }
.hero-line {
  stroke: var(--accent);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0.42;
  stroke-dasharray: 1;
  stroke-dashoffset: 0;
  animation: hero-line-draw 1.7s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both;
}
.hero-dot { fill: var(--accent); opacity: 0.4; animation: hero-fade-in 0.6s ease 1.5s both; }
.hero-peak { fill: var(--accent); opacity: 1; animation: hero-fade-in 0.6s ease 1.75s both; }
/* The "now" marker (2024 all-time high) gets a slow ambient pulse once it
   lands — the one live-feeling beat that sells "alive, and getting stronger". */
.hero-peak-halo {
  fill: none; stroke: var(--accent); stroke-width: 1.25; opacity: 0.45;
  transform-box: fill-box; transform-origin: center;
  animation: hero-fade-in 0.6s ease 1.75s both, hero-halo-pulse 3.4s ease-in-out 2.5s infinite;
}
@keyframes hero-halo-pulse {
  0%, 100% { transform: scale(1); opacity: 0.45; }
  50%      { transform: scale(1.35); opacity: 0.12; }
}
@keyframes hero-line-draw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
/* Only a `from` frame: the 100% is each element's own base opacity, so one
   keyframe fades every mark in to its own resting value. */
@keyframes hero-fade-in { from { opacity: 0; } }
@media (prefers-reduced-motion: reduce) {
  .hero-bg-chart * { animation: none !important; }
}
.hero-content { position: relative; z-index: 1; max-width: 768px; }
.hero-eyebrow {
  font-family: var(--font-mono);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 28px;
}
.hero-eyebrow .dot {
  display: inline-block;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--accent);
  animation: pulse 2.4s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.45; transform: scale(0.7); }
}
.hero-title {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: clamp(46px, 7.6vw, 96px);
  line-height: 0.96;
  letter-spacing: -0.018em;
  margin: 0 0 32px;
  color: var(--text);
}
.hero-line-1 {
  display: block;
  font-style: normal;
}
.hero-line-2 {
  display: block;
  font-style: normal;
  color: var(--accent);
  margin-top: 4px;
}
.hero-line-2 .display-italic { color: var(--accent); }
.hero-sub {
  max-width: 560px;
  margin: 0 0 36px;
  font-size: 17px;
  line-height: 1.6;
  /* Near-full-strength so it stays legible over the banner ridge behind it. */
  color: var(--text);
  opacity: 0.92;
}
.hero-cta { display: flex; gap: 18px; align-items: center; flex-wrap: wrap; }
.cta-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14.5px;
  font-weight: 500;
  padding: 14px 22px;
  border-radius: 999px;
  text-decoration: none;
  background: var(--accent);
  color: #0d1014;
  transition: transform 0.18s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.18s ease;
  box-shadow: 0 1px 0 rgba(255,255,255,0.1) inset, 0 4px 14px rgba(247, 129, 102, 0.25);
}
.cta-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 1px 0 rgba(255,255,255,0.15) inset, 0 8px 22px rgba(247, 129, 102, 0.35);
}
.cta-primary svg { transition: transform 0.18s ease; }
.cta-primary:hover svg { transform: translateX(2px); }
.cta-secondary {
  font-size: 14px;
  color: var(--text-dim);
  text-decoration: none;
  border-bottom: 1px solid var(--border);
  padding-bottom: 2px;
  transition: color 0.18s ease, border-color 0.18s ease;
}
.cta-secondary:hover { color: var(--text); border-bottom-color: var(--accent); }

/* ───── Hero inline lifter lookup ─────
   The fastest route to the thing most visitors actually want — their own or
   a rival's profile. A real search field, not another button. */
/* z-index lifts the whole search (and its absolutely-positioned results)
   into its own stacking context above the CTA row that follows it in the
   DOM — otherwise the dropdown paints behind the buttons. */
.hero-search { position: relative; z-index: 40; max-width: 520px; margin: 0 0 28px; }
.hs-field {
  display: flex; align-items: center; gap: 11px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 13px 16px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.hero-search:focus-within .hs-field,
.hero-search.open .hs-field { border-color: var(--accent-2); }
.hero-search:focus-within .hs-field { box-shadow: 0 0 0 3px var(--accent-2-soft); }
.hs-icon { color: var(--accent); flex: 0 0 auto; }
.hs-input {
  flex: 1; min-width: 0;
  background: transparent; border: 0; padding: 0;
  font-family: var(--font-body); font-size: 16px; color: var(--text);
  outline: none;
}
.hs-input::placeholder { color: var(--text-dim); }
.hs-spin {
  width: 15px; height: 15px; flex: 0 0 auto;
  border: 2px solid var(--border); border-top-color: var(--accent);
  border-radius: 50%; animation: hs-spin 0.7s linear infinite;
}
@keyframes hs-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .hs-spin { animation: none; } }
.hs-results, .hs-empty {
  position: absolute; top: calc(100% + 8px); left: 0; right: 0; z-index: 20;
  background: var(--bg-elev-2);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.5);
}
.hs-results { margin: 0; padding: 6px; list-style: none; }
.hs-row {
  display: flex; justify-content: space-between; align-items: baseline; gap: 16px;
  padding: 10px 12px; border-radius: 8px; cursor: pointer;
}
.hs-row.active { background: var(--bg-elev); }
.hs-name { font-size: 15px; color: var(--text); font-weight: 500; }
.hs-meta {
  font-family: var(--font-mono); font-size: 11.5px; color: var(--text-dim);
  white-space: nowrap; font-variant-numeric: tabular-nums;
}
.hs-dots { color: var(--accent); }
.hs-dot-sep { color: var(--text-mute); margin: 0 3px; }
.hs-empty { padding: 14px 16px; font-size: 13.5px; color: var(--text-dim); }

/* ───── STATS — editorial table ───── */
.stats {
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  padding: 28px 0;
  margin-bottom: 40px;
}
.stats-eyebrow {
  font-family: var(--font-mono);
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-mute);
  margin-bottom: 18px;
}
.stats-table {
  margin: 0;
  display: flex;
  flex-direction: column;
}
.stat-row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: baseline;
  gap: 24px;
  padding: 14px 0;
  border-top: 1px solid var(--border-soft);
}
.stat-row:first-child { border-top: none; padding-top: 6px; }
.stat-row dt {
  font-size: 14px;
  color: var(--text-dim);
  font-weight: 400;
}
.stat-row dd {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 32px;
  font-weight: 500;
  letter-spacing: -0.02em;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

/* ───── Section headers (editorial pattern) ───── */
.section-eyebrow {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 16px;
}
.section-headline {
  font-family: var(--font-display);
  font-style: normal;
  font-weight: 400;
  font-size: clamp(28px, 3.5vw, 40px);
  line-height: 1.1;
  letter-spacing: -0.012em;
  margin: 0 0 32px;
  max-width: 780px;
}
.section-headline .display-italic {
  color: var(--accent);
}

/* ───── Talking points ───── */
/* ───── Talking points — clickable, each links to where it's answered ─────
   Same interactive language as the feature cards, but distinguished by a
   LEFT accent rail (features use a top rail) and a --text question heading. */
.talking-points { padding: 16px 0; }
.point-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}
.point {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 22px 24px 20px 26px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 14px;
  text-decoration: none;
  color: inherit;
  overflow: hidden;
  transition: transform 0.24s cubic-bezier(0.22, 1, 0.36, 1),
              border-color 0.24s ease, box-shadow 0.24s ease;
}
.point::before {
  content: '';
  position: absolute;
  top: 0; left: 0; bottom: 0;
  width: 3px;
  background: var(--fa);
  transform: scaleY(0);
  transform-origin: top;
  transition: transform 0.34s cubic-bezier(0.22, 1, 0.36, 1);
}
.point:hover,
.point:focus-visible {
  transform: translateY(-4px);
  border-color: var(--fa);
  box-shadow: 0 18px 44px -20px rgba(0, 0, 0, 0.75);
  outline: none;
}
.point:hover::before,
.point:focus-visible::before { transform: scaleY(1); }
.point-tag {
  font-family: var(--font-mono);
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--fa);
  font-weight: 500;
  margin-bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.point-tag .dot {
  width: 5px; height: 5px;
  border-radius: 50%;
  background: var(--fa);
}
.point h3 {
  font-family: var(--font-display);
  font-weight: 400;
  margin: 0 0 10px;
  font-size: 23px;
  line-height: 1.15;
  letter-spacing: -0.005em;
  color: var(--text);
}
.point p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-dim);
}
.point-go {
  margin-top: 16px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: var(--font-mono);
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fa);
}
.point-go svg { transition: transform 0.2s ease; }
.point:hover .point-go svg,
.point:focus-visible .point-go svg { transform: translateX(4px); }

/* ───── Feature highlights — four clickable "lenses" ─────
   Each card links to its tool and carries a signature hue (--fa) from the
   chart palette. Hover lifts the card, floods a top rail across it, and
   slides the CTA arrow; v-reveal staggers them in on scroll. */
.features { padding: 16px 0; }
.feature-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}
.feature {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 22px 24px 20px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 14px;
  text-decoration: none;
  color: inherit;
  overflow: hidden;
  transition: transform 0.24s cubic-bezier(0.22, 1, 0.36, 1),
              border-color 0.24s ease, box-shadow 0.24s ease;
}
/* Accent rail that floods across the top edge on hover. */
.feature::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  height: 3px;
  width: 0;
  background: var(--fa);
  transition: width 0.34s cubic-bezier(0.22, 1, 0.36, 1);
}
.feature:hover,
.feature:focus-visible {
  transform: translateY(-4px);
  border-color: var(--fa);
  box-shadow: 0 18px 44px -20px rgba(0, 0, 0, 0.75);
  outline: none;
}
.feature:hover::before,
.feature:focus-visible::before { width: 100%; }
.feature-num {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--fa);
  opacity: 0.85;
  letter-spacing: 0.16em;
  display: block;
  margin-bottom: 12px;
}
.feature h3 {
  font-family: var(--font-display);
  font-weight: 400;
  font-style: italic;
  font-size: 30px;
  letter-spacing: -0.01em;
  color: var(--fa);
  margin: 0 0 12px;
  line-height: 1;
}
.feature-summary {
  font-size: 14px;
  line-height: 1.55;
  color: var(--text);
  margin: 0 0 14px;
}
.feature ul {
  margin: 0 0 18px;
  padding-left: 16px;
  font-size: 13px;
  color: var(--text-dim);
  line-height: 1.6;
}
.feature ul li { margin-bottom: 5px; }
.feature ul li::marker { color: var(--fa); }
.feature-go {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: var(--font-mono);
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fa);
}
.feature-go svg { transition: transform 0.2s ease; }
.feature:hover .feature-go svg,
.feature:focus-visible .feature-go svg { transform: translateX(4px); }

/* ───── Contact ───── */
.contact {
  padding: 24px 0 80px;
  text-align: left;
}
.contact p {
  max-width: 580px;
  font-size: 15px;
  line-height: 1.65;
  color: var(--text-dim);
}
.contact-email {
  color: var(--accent);
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid currentColor;
}
.contact-email:hover { color: var(--text); }

/* ───── Mobile ───── */
@media (max-width: 768px) {
  .hero { padding: 64px 18px 56px; margin-bottom: 24px; }
  .hero-title { margin-bottom: 24px; }
  .hero-sub { font-size: 15px; }
  .hero-search { max-width: none; }
  .hero-bg-chart { opacity: 0.7; }
  .stats { padding: 22px 0; margin-bottom: 28px; }
  .stat-row { padding: 10px 0; }
  .stat-row dd { font-size: 26px; }
  .section-headline { margin-bottom: 22px; }
  .point h3 { font-size: 20px; }
  .feature h3 { font-size: 26px; }
  .feature, .point { padding: 18px 18px 16px 20px; }
  .feature-grid, .point-grid { grid-template-columns: 1fr; gap: 14px; }
  .contact { padding: 16px 0 60px; }
  .cta-primary { padding: 12px 18px; }
}
</style>
